import { create } from "zustand";
import type { IViewConfig } from "lola-framework-ui-test";
import type { IErrorState } from "../shared/error/interfaces/error";
import { errorInitialState, themeFormattedInitialState } from "../constants";
import type { IPaymentState } from "../interfaces/payment";
import type { ICardState } from "../interfaces/card";
import type { FundingInstrument } from "../interfaces/funding";

export type AmountAnchor = "send" | "receive";

export interface IAmountState {
  /** Pinned leg amount (source ccy when anchor=send, destination when receive). */
  amount: string;
  /** Which leg `amount` is pinned to. Last-edited side wins. */
  anchor: AmountAnchor;
}

interface GlobalState {
  theme: IViewConfig;
  setTheme: (theme: IViewConfig) => void;

  linkId: string | null;
  setLinkId: (linkId: string | null) => void;

  errorState: IErrorState;
  setErrorState: (errorState: IErrorState) => void;

  amountState: IAmountState;
  setAmountState: (amountState: IAmountState) => void;

  paymentState: IPaymentState;
  setPaymentState: (paymentState: IPaymentState) => void;

  cardState: ICardState;
  setCardState: (cardState: ICardState) => void;

  cardList: ICardState[];
  setCardList: (cardList: ICardState[]) => void;

  /** Saved or pre-validated (new) funding card for lazy rail prepare. */
  fundingInstrument: FundingInstrument | null;
  setFundingInstrument: (fundingInstrument: FundingInstrument | null) => void;

  /** Set after successful prepareFundingRail (CARD only). */
  preparedFundingPaymentMethodId: string | null;
  setPreparedFundingPaymentMethodId: (id: string | null) => void;
}

export const useStore = create<GlobalState>((set) => ({
  theme: themeFormattedInitialState,
  setTheme: (theme) => {
    set(() => ({ theme }));
  },

  linkId: null,
  setLinkId: (linkId) => {
    set(() => ({ linkId }));
  },

  errorState: errorInitialState,
  setErrorState: (errorState) => {
    set(() => ({ errorState }));
  },

  amountState: { amount: "", anchor: "send" },
  setAmountState: (amountState) => {
    set(() => ({ amountState }));
  },

  paymentState: { payment: null },
  setPaymentState: (paymentState) => {
    set(() => ({ paymentState }));
  },

  cardState: {
    cardId: null,
    name: null,
    brand: null,
    last4: null,
    accountNumber: null,
    expirationDate: null,
    fullName: null,
    cvv: null,
  },
  setCardState: (cardState) => {
    set(() => ({ cardState }));
  },

  cardList: [],
  setCardList: (cardList) => {
    set(() => ({ cardList }));
  },

  fundingInstrument: null,
  setFundingInstrument: (fundingInstrument) => {
    set(() => ({ fundingInstrument }));
  },

  preparedFundingPaymentMethodId: null,
  setPreparedFundingPaymentMethodId: (preparedFundingPaymentMethodId) => {
    set(() => ({ preparedFundingPaymentMethodId }));
  },
}));
